'use client'
import { useMemoizedContentGetter } from 'utils/contentful'
import { TypographyLongForm as Typography, MediaImageStandard } from 'components/Contentful'
import Section from 'components/Section'
import { Grid, Box, Button } from '@achieve/ascend'
import classNames from 'classnames'
import { ArrowRight } from 'react-feather'
import { Eyebrow } from 'components/Eyebrow'
import { actualDevice, DEVICES } from 'utils/shared'
import useIsSsr from 'hooks/useIsSsr'
import { useState, useEffect } from 'react'
import { AchieveLink } from 'components/AchieveLink'
import { mainProductConfig } from 'constants/mainProductConfig'
import { getSlug } from 'utils/conversions'
import { Countdown } from 'components/Countdown'

function SectionFullBackground({ content }) {
  const sectionVariationObject = {
    ...content?.fields?.experiment,
    variant: content?.fields?.experimentVariation,
  }
  const isSsr = useIsSsr()
  const [device, setDevice] = useState(null)

  useEffect(() => {
    if (!isSsr) {
      // check If current browser is a safari browser IOS
      setDevice(actualDevice(navigator.userAgent))
    }
  }, [isSsr])

  const {
    title,
    subTitle,
    background,
    link,
    button_google,
    button_apple,
    image_google,
    image_apple,
    eyebrow,
    backgroundConfigJson,
    countdownImage,
  } = useMemoizedContentGetter(content, [
    'title',
    'subTitle',
    'background',
    'link',
    'eyebrow',
    'button_google',
    'button_apple',
    'image_google',
    'image_apple',
    'backgroundConfigJson',
    'countdownImage',
  ])
  const jsonContent = backgroundConfigJson?.jsonContent || backgroundConfigJson
  const siteEnv = process.env.NEXT_PUBLIC_SITE_ENVIRONMENT || 'achieve'
  const backgroundObjectPosition = jsonContent?.backgroundObjectPosition ?? 'right'
  const backgroundObjectPositionMobile = jsonContent?.backgroundObjectPositionMobile ?? 'bottom'
  const mobilePadding = jsonContent?.mobilePadding ?? 'bottom'
  const {
    hideCTAButtonArrow = false,
    ctaLinkSx = null,
    eyebrowSecondaryColor = false,
    showCounter = false,
    counterDate = '20/05/2026 12:30:30',
  } = jsonContent || {}

  return (
    <Section
      className={`[content-visibility:auto] ${
        jsonContent?.sectionContainerClassNames ? jsonContent?.sectionContainerClassNames : ''
      }`}
      data-testid="closer-fullbg-section"
      data-narrow={true}
      mobileMediaContent={background?.mobileMediaContent || background?.mobileMediaContent}
      middleMediaContent={background?.middleMediaContent || background?.mobileMediaContent}
      mediaContent={background?.mediaContent}
      backgroundImageAlt={background?.mediaAltText}
      showPattern={jsonContent?.showPattern}
      backgroundObjectPositionclassName={`bg-${getSlug(
        backgroundObjectPositionMobile
      )} lg:bg-${getSlug(backgroundObjectPosition)}`}
      variant={jsonContent?.background ?? 'bgDefault'}
      full={true}
      childContainerClassName={jsonContent?.childContainerClassName || ''}
    >
      <Box
        className={classNames(
          mobilePadding === 'bottom' ? 'pt-8 pb-[26px] px-8' : '',
          mobilePadding === 'top' ? 'pt-[336px] pb-8 px-8' : '',
          'relative h-full flex items-center justify-center flex-col text-center md:p-0 lg:max-w-[1007px] lg:w-[1007px] lg:py-8 2xl:max-w-full 2xl:w-full 2xl:m-0'
        )}
        component="div"
      >
        <Grid
          sx={{ display: 'flex', alignItems: 'center' }}
          className={classNames(
            jsonContent?.longer ? 'min-h-[520px] w-full' : 'min-h-[280px] md:min-h-[360px] w-full',
            showCounter &&
              'flex flex-col items-center md:flex-row md:items-center md:justify-between'
          )}
          data-testid="closer-fullbg-outer-grid-container"
        >
          <>
            {jsonContent?.textAlign == 'right' && (
              <Grid
                size={{ xs: 4, md: 6, lg: 7 }}
                className="hidden sm:block lg:basis-7/12 md:basis-1/2  "
              ></Grid>
            )}

            <Grid
              size={{ xs: 12, md: 6, lg: 5 }}
              className={'text-left lg:basis-5/12 md:basis-1/2 basis-full'}
              data-testid="closer-fullbg-grid-item-text"
            >
              <div
                className={classNames('px-0 py-0', jsonContent?.textAlign == 'right' && 'md:pl-10')}
                data-testid="closer-fullbg-text-container"
              >
                {eyebrow && (
                  <Eyebrow
                    content={eyebrow?.textContent}
                    variant="bodySm"
                    fontWeight="bold"
                    component="h1"
                    useCustomColor={jsonContent?.eyebrowUseCustomColor || false}
                    black={eyebrowSecondaryColor}
                    className={classNames(
                      'text-left! pb-0',
                      jsonContent?.eyebrowClassName
                        ? jsonContent?.eyebrowClassName
                        : !eyebrow?.fontColor ||
                            (eyebrow?.fontColor?.toLowerCase() === 'black' && 'text-[#1d252f]'),
                      eyebrow?.fontColor?.toLowerCase() === 'white' && 'text-white',
                      eyebrow?.fontColor?.toLowerCase() === 'blue' && 'text-[#30f]',
                      showCounter
                        ? '[align-self:auto!important] leading-[105%] tracking-[-1.12px]!'
                        : ''
                    )}
                  />
                )}
                {jsonContent?.blur && <div></div>}
                {title && (
                  <Typography
                    component="h2"
                    content={title?.textContent}
                    className={classNames(
                      jsonContent?.titleClassName ? jsonContent?.titleClassName : '',
                      'md:relative md:z-2 ',
                      !title?.fontColor ||
                        (title?.fontColor?.toLowerCase() === 'black' && 'text-[#1d252f]'),
                      title?.fontColor?.toLowerCase() === 'white' && 'text-white',
                      title?.fontColor?.toLowerCase() === 'blue' && 'text-[#30f]',
                      showCounter ? 'leading-[105%] tracking-[-0.02em]' : ''
                    )}
                    data-testid="section-full-background-title"
                  />
                )}
                {subTitle && (
                  <Typography
                    content={subTitle?.textContent}
                    className={classNames(
                      jsonContent?.subTitleClassName ? jsonContent?.subTitleClassName : '',
                      'md:relative md:z-2 lg:my-7 md:my-[23px] my-7',
                      !subTitle?.fontColor ||
                        (subTitle?.fontColor?.toLowerCase() === 'black' && 'text-[#1d252f]'),
                      subTitle?.fontColor?.toLowerCase() === 'white' && 'text-white',
                      subTitle?.fontColor?.toLowerCase() === 'blue' && 'text-[#30f]'
                    )}
                    data-testid="section-full-background-subtitle"
                  />
                )}
              </div>
              {link && (
                <div className="flex">
                  <Button
                    color="primary"
                    variant="contained"
                    underline="hover"
                    fontWeight="bold"
                    href={link?.linkHref}
                    className="flex flex-row items-center gap-2 w-full sm:w-auto"
                  >
                    <Typography
                      content={link?.linkText}
                      fontWeight="bold"
                      className="inline mb-0!"
                      variant="bodyLg"
                      sx={ctaLinkSx}
                    />
                    {!hideCTAButtonArrow && <ArrowRight className={'pt-1'} />}
                  </Button>
                </div>
              )}
              {(button_google || button_apple) && (
                <Grid className={'relative h-[38px] z-2 mt-6 mb-[37.6px]'} container>
                  {(device === DEVICES.desktop || device === DEVICES.ios) && image_apple && (
                    <AchieveLink
                      href={button_apple?.linkHref}
                      data-testid="app-store-btn"
                      track={{
                        click_id: image_google?.mediaAltText || 'native-download-google-en',
                        click_text: `${
                          mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']
                        } | ${image_google?.mediaAltText || 'native-download-google-en'}`,
                        click_type: 'Button Click',
                        click_position: 1,
                        event_type: 'button_click',
                        nav_link_section: 'Closer Statement',
                        track_event: 'app_download_click',
                        sectionVariationObject,
                      }}
                    >
                      <div className={'h-[38px] w-[118px] mr-4'}>
                        <MediaImageStandard
                          content={image_apple?.mediaContent}
                          alt={image_apple?.mediaAltText || 'apple-button-image'}
                        />
                      </div>
                    </AchieveLink>
                  )}
                  {(device === DEVICES.desktop || device === DEVICES.android) && image_google && (
                    <AchieveLink
                      href={button_google?.linkHref}
                      data-testid="google-play-btn"
                      track={{
                        click_id: image_apple?.mediaAltText || 'native-download-apple-en',
                        click_text: `${
                          mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']
                        } | ${image_apple?.mediaAltText || 'native-download-apple-en'}`,
                        click_type: 'Button Click',
                        click_position: 1,
                        event_type: 'button_click',
                        nav_link_section: 'Closer Statement',
                        track_event: 'app_download_click',
                        sectionVariationObject,
                      }}
                    >
                      <div className={'w-[134px] h-[38px]'}>
                        <MediaImageStandard
                          content={image_google?.mediaContent}
                          alt={image_google?.mediaAltText || 'google-button-image'}
                        />
                      </div>
                    </AchieveLink>
                  )}
                </Grid>
              )}
            </Grid>
          </>
          {showCounter && (
            <div className="flex pt-[180px] md:pt-0 mb-[50px] justify-center md:justify-end lg:justify-center">
              <Countdown time={counterDate} image={countdownImage} />
            </div>
          )}
        </Grid>
      </Box>
    </Section>
  )
}
export { SectionFullBackground }
export default SectionFullBackground
